import styled, { css } from 'styled-components';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps {
  $variant?: Variant;
  $size?: Size;
  $fullWidth?: boolean;
}

const variantStyles: Record<Variant, ReturnType<typeof css>> = {
  primary: css`
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.onPrimary};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.primaryHover};
    }
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.surfaceMuted};
    color: ${({ theme }) => theme.colors.textPrimary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.surfaceHover};
      border-color: ${({ theme }) => theme.colors.borderStrong};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.textSecondary};
    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.colors.surfaceHover};
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  `,
  danger: css`
    background: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.mode === 'dark' ? '#171f2e' : '#fff'};
    &:hover:not(:disabled) {
      filter: brightness(1.08);
    }
  `,
} as const;

const sizeStyles: Record<Size, ReturnType<typeof css>> = {
  sm: css`
    min-height: 36px;
    padding: 0 12px;
    font-size: 13px;
  `,
  md: css`
    min-height: 44px;
    padding: 0 18px;
    font-size: 14px;
  `,
  lg: css`
    min-height: 50px;
    padding: 0 24px;
    font-size: 15px;
  `,
} as const;

export const Button = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-weight: 600;
  line-height: 1.35;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    filter 0.15s ease,
    opacity 0.15s ease;
  white-space: nowrap;
  flex-shrink: 0;

  svg { flex-shrink: 0; }

  ${({ $variant = 'primary' }: ButtonProps) => variantStyles[$variant]}
  ${({ $size = 'md' }: ButtonProps) => sizeStyles[$size]}
  ${({ $fullWidth }: ButtonProps) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  &:disabled {
    opacity: 0.55;
  }

  @media (pointer: coarse) {
    min-height: 44px;
  }
`;
